import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  quizTypesService,
  type CreateQuizTypeRequest,
  type UpdateQuizTypeRequest,
} from "@/api/services/quiz-types.service";

export const quizTypeKeys = {
  all: ["quiz-types"] as const,
  admin: () => [...quizTypeKeys.all, "admin"] as const,
  public: () => [...quizTypeKeys.all, "public"] as const,
};

export function useQuizTypesAdmin() {
  return useQuery({
    queryKey: quizTypeKeys.admin(),
    queryFn: () => quizTypesService.listAdmin(),
    staleTime: 30_000,
  });
}

export function useQuizTypesPublic() {
  return useQuery({
    queryKey: quizTypeKeys.public(),
    queryFn: () => quizTypesService.listPublic(),
    staleTime: 60_000,
  });
}

export function useCreateQuizType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateQuizTypeRequest) => quizTypesService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: quizTypeKeys.all }),
  });
}

export function useUpdateQuizType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateQuizTypeRequest }) =>
      quizTypesService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: quizTypeKeys.all }),
  });
}

export function useToggleQuizType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => quizTypesService.toggle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: quizTypeKeys.all }),
  });
}

export function useDeleteQuizType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => quizTypesService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: quizTypeKeys.all }),
  });
}

export function useReorderQuizTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => quizTypesService.reorder(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: quizTypeKeys.all }),
  });
}
